import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class QuickAddBasketV1Service {
  private quickAddBasketObject: any;
  private _instantGroceryBasketQty = new Subject<number>();
  public instantGroceryBasketQty$ = this._instantGroceryBasketQty.asObservable();
  private _selectAllItems = new Subject<boolean>();
  public selectAllItems$ = this._selectAllItems.asObservable();
  private _redirectToLandingPage = new Subject<boolean>();
  public redirectToLandingPage$ = this._redirectToLandingPage.asObservable();
  static readonly QUICK_ADD_BANNER_CONST = 'quickAddBasket';
  private _isUserInGroup = new Subject<boolean>();
  public isUserInGroup$ = this._isUserInGroup.asObservable();

  constructor() {
  }

  /**
   * Initializes the quick add basket with the provided products.
   * Each product is transformed into a basket item with a default quantity of 1 and selected state.
   * If a product is sold by weight and has a specific display type, a selected weight is also set.
   * 
   * @param products The array of products to initialize the basket with.
   * @returns An object representing the initialized quick add basket.
   */
  private initializeQuickAddBasket(products: any[]): { cartItemsList: any[] } {
    const quickAddBasket = products.map((x) => {
      const item: any = {
        itemId: x.id,
        qty: 1,
        selected: true
      }
      if (x?.sellByWeight === 'W' && x?.displayType === '3') {
        item.selectedWeight = Number(x.minWeight).toFixed(2);
      }
      return item;
    })
    return { cartItemsList: quickAddBasket };
  }

  /**
  * Processes the quick add basket by either initializing it with the provided products
  * if it doesn't exist or updating the existing basket. It ensures that the basket
  * contains only items present in the provided products list and adds any new items
  * from the products list that are not already in the basket.
  * 
  * @param products The array of products to process against the quick add basket.
  */
  public processQuickAddBasket(products: any[]): void {
    let quickAddBasket = JSON.parse(localStorage.getItem(QuickAddBasketV1Service.QUICK_ADD_BANNER_CONST));

    // Create new basket if one doesn't exist.
    if (!quickAddBasket || quickAddBasket == '') {
      quickAddBasket = this.initializeQuickAddBasket(products);
    } else {
      // Remove any item in quick add basket not found in /smartbasket API response.
      quickAddBasket.cartItemsList = quickAddBasket.cartItemsList.filter(cartItem => (products.some(x => cartItem.itemId == x.id)));

      products.forEach((smartBasketResponseItem) => {
        const curItemId = smartBasketResponseItem.id;
        const curItemMinWeight = smartBasketResponseItem?.minWeight;
        // Item is in API response but not in quick add basket
        if (quickAddBasket.cartItemsList.filter(item => item.itemId == smartBasketResponseItem.id).length == 0) {
          // Add the item to local storage.
          smartBasketResponseItem?.sellByWeight === 'W' && smartBasketResponseItem?.displayType === '3' ?
            quickAddBasket.cartItemsList.push({ itemId: curItemId, qty: 1, selectedWeight: Number(curItemMinWeight).toFixed(2), selected: true }) :
            quickAddBasket.cartItemsList.push({ itemId: curItemId, qty: 1, selected: true });
        }
      });
    }

    this.setQuickAddBasket(quickAddBasket);
  }

  /**
   * Retrieves all selected products from the quick add basket.
   * If a product has a selected weight, it returns the item ID and selected weight;
   * otherwise, it returns the item ID and quantity.
   * 
   * @returns An array of selected products from the quick add basket.
   */
  public getAllSelectedProducts(): any[] {
    const products = this.quickAddBasketObject.cartItemsList.filter((item) => (item.selected)).map((item) => {
      if (item?.selectedWeight) {
        return {
          itemId: item.itemId,
          selectedWeight: item.selectedWeight
        }
      }

      return {
        itemId: item.itemId,
        qty: item.qty
      }

    });
    return products;
  }

  /**
   * Checks if all products in the quick add basket are selected.
   * 
   * @returns A boolean indicating whether all products are selected.
   */
  public areAllProductsSelected(): boolean {
    return this.quickAddBasketObject.cartItemsList.every((item) => item.selected);
  }

  /**
   * Clears the quick add basket from local storage.
   */
  public clearQuickAddBasket(): void {
    localStorage.removeItem(QuickAddBasketV1Service.QUICK_ADD_BANNER_CONST);
    this.quickAddBasketObject = null;
  }

  /**
   * Checks if a product is selected in the quick add basket.
   * 
   * @param productId The ID of the product to check.
   * @returns A boolean indicating whether the product is selected.
   */
  public isProductSelected(productId: string): boolean {
    return this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == productId))?.selected == true;
  }

  /**
  * Sets the selected state of a product in the quick add basket.
  * 
  * @param id The ID of the product to update.
  * @param state The new selected state of the product.
  */
  public setQuickAddBasketSelectedProperty(id: string, state: boolean): void {
    this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == id)).selected = state;
    this.setQuickAddBasket(this.quickAddBasketObject);
  }

  /**
   * Updates the selected state of a product in the quick add basket and notifies subscribers.
   * 
   * @param id The ID of the product to update.
   * @param state The new selected state of the product.
   */
  public updateQuickAddBasketSelectedProperty(id: string, state: boolean): void {
    this.setQuickAddBasketSelectedProperty(id, state);
  }
  /**
   * Sets the quantity of a product in the quick add basket.
   * 
   * @param id The ID of the product to update.
   * @param qty The new quantity of the product.
   */
  public setQuickAddBasketItemQuantity(id: string, qty: number): void {
    this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == id)).qty = qty;
    this.setQuickAddBasket(this.quickAddBasketObject);
  }

  public getQuickAddBasketItemQuantity(id: string): number {
    return this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == id))?.qty
  }

  /**
   * Sets the weight of a product in the quick add basket.
   * 
   * @param id The ID of the product to update.
   * @param weight The new weight of the product.
   */
  public setQuickAddBasketItemWeight(id: string, weight: number): void {
    const item = this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == id));
    item.selectedWeight = weight.toFixed(2);
    this.setQuickAddBasket(this.quickAddBasketObject);
  }


  /**
   * Gets the weight of a product in the quick add basket.
   * 
   * @param id The ID of the product.
   * @returns The weight of the product.
   */
  public getQuickAddBasketItemWeight(id: string): string {
    return this.quickAddBasketObject.cartItemsList.find((item) => (item.itemId == id)).selectedWeight;
  }

  /**
   * Returns total amount of unique items in the quick add basket.
   */
  public getAmountOfQuickAddItems(): number {
    return this.quickAddBasketObject.cartItemsList.length;
  }

  /**
   * Unselects all items in the quick add basket and notifies subscribers.
   */
  public unselectAllItems(): void {
    this.quickAddBasketObject.cartItemsList = this.quickAddBasketObject.cartItemsList.map(item => ({
      ...item,
      selected: false
    }));
    this.setQuickAddBasket(this.quickAddBasketObject);
    this._selectAllItems.next(false);
  }


  /**
   * Selects all items in the quick add basket and notifies subscribers.
   */
  public selectAllItems(): void {
    this.quickAddBasketObject.cartItemsList = this.quickAddBasketObject.cartItemsList.map(item => ({
      ...item,
      selected: true
    }));
    this.setQuickAddBasket(this.quickAddBasketObject);
    this._selectAllItems.next(true);
  }

  public redirectToLandingPage(): void {
    this._redirectToLandingPage.next(true);
  }
  /**
     * Sets the quick add basket object and updates local storage.
     * 
     * @param basket The quick add basket object to set.
     */
  private setQuickAddBasket(basket: any) {
    this.quickAddBasketObject = basket;
    this.setQuickAddBasketLocalStorageObject(basket);
    this._instantGroceryBasketQty.next(this.calculateTotalQuickAddBasketQty(basket))
  }

  /**
   * Updates the quick add basket object in local storage.
   * 
   * @param basket The quick add basket object to store.
   */
  private setQuickAddBasketLocalStorageObject(basket: any) {
    localStorage.setItem(QuickAddBasketV1Service.QUICK_ADD_BANNER_CONST, JSON.stringify(basket));
  }


  /**
   * Calculates the total quantity of selected items in the quick add basket.
   * 
   * @param basket The quick add basket object.
   * @returns The total quantity of selected items.
   */
  private calculateTotalQuickAddBasketQty(basket): number {
    const qscItems = basket.cartItemsList;
    return qscItems.filter((item) => (item.selected)).reduce((sum, item) => sum + item.qty, 0);
  }

  public notifyUserInGroup(isUserInGroup: boolean): void {
    this._isUserInGroup.next(isUserInGroup);
  }
}
